import { getApiErrorMessage } from '../services/api';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, XCircle, AlertTriangle, Loader2, Info } from 'lucide-react';
import axios from 'axios';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ExternalApprovalPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') || '';
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(null); // 'approve' | 'reject'
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null); // 'approved' | 'rejected' | 'forwarded'
  const [extSort, handleExtSort] = useSortState('', 'asc');

  useEffect(() => {
    if (!token) { setError('No approval token provided.'); setLoading(false); return; }
    axios.get(`${API}/api/external-approval/document`, { params: { token } })
      .then(r => setDoc(r.data))
      .catch(e => setError(getApiErrorMessage(e, 'This approval link is invalid or has expired.')))
      .finally(() => setLoading(false));
  }, [token]);

  const handleAction = async () => {
    if (mode === 'reject' && !notes.trim()) { return; }
    setSubmitting(true);
    try {
      const endpoint = mode === 'approve' ? 'approve' : 'reject';
      const r = await axios.post(`${API}/api/external-approval/${endpoint}`, { notes }, { params: { token } });
      setDone(r.data.status);
    } catch (e) {
      setError(getApiErrorMessage(e, 'Action failed. The link may have expired.'));
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <Info className="h-12 w-12 text-blue-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Unable to Approve</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          {done === 'rejected' ? (
            <XCircle className="h-14 w-14 text-red-500 mx-auto mb-4" />
          ) : (
            <CheckCircle className="h-14 w-14 text-green-500 mx-auto mb-4" />
          )}
          <h1 className="text-xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk' }}>
            {done === 'approved' ? 'Requisition Fully Approved' : done === 'forwarded' ? 'Approved — Forwarded to Next Approver' : 'Requisition Rejected'}
          </h1>
          <p className="text-sm text-slate-600">
            {done === 'rejected'
              ? 'The requisition has been returned to the originator for revision.'
              : done === 'forwarded'
              ? 'Your approval has been recorded. The requisition has been forwarded to the next approver in the chain.'
              : 'All approvals are complete. The requisition is now fully approved.'}
          </p>
          <p className="text-xs text-slate-400 mt-6">You can close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-[#1e3a5f] text-white rounded-t-2xl px-8 py-6">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Approval Required</h1>
          <p className="text-slate-300 text-sm mt-1">Requisition {doc.request_number}</p>
        </div>

        <div className="bg-white rounded-b-2xl shadow-lg">
          {/* Greeting */}
          <div className="px-8 pt-6 pb-4 border-b">
            <p className="text-slate-700">Hello <strong>{doc.approver_name}</strong>, please review this requisition and approve or reject it.</p>
          </div>

          {/* Requisition Summary */}
          <div className="px-8 py-5">
            <div className="grid grid-cols-2 gap-4 text-sm mb-5">
              {[
                ['Document Number', doc.request_number],
                ['Reference', doc.ref_num],
                ['Description', doc.request_description],
                ['Category', doc.category_code],
                ['Document Type', doc.document_type],
                ['Delivery To', doc.delivery_to],
                ['Entry Date', doc.entry_date],
                ['Status', doc.status],
              ].filter(([, v]) => v).map(([label, value]) => (
                <div key={label}>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
                  <p className="text-slate-800 font-medium">{value}</p>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between bg-[#1e3a5f] text-white rounded-xl px-5 py-3 mb-6">
              <span className="text-sm font-semibold">Total Estimated Value</span>
              <span className="text-lg font-bold">USD {(doc.request_net_total || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
            </div>

            {/* Line Items */}
            <h3 className="text-sm font-bold text-[#1e3a5f] mb-2">Line Items ({(doc.line_items || []).length})</h3>
            <table className="w-full text-xs border-collapse mb-6">
              <thead>
                <tr className="bg-slate-100">
                  <SortableTableHeader label="Item" field="item_num" sortState={extSort} onSort={handleExtSort} />
                  <SortableTableHeader label="Description" field="item_description" sortState={extSort} onSort={handleExtSort} />
                  <SortableTableHeader label="Qty" field="quantity" sortState={extSort} onSort={handleExtSort} className="text-right" />
                  <SortableTableHeader label="Unit" field="unit" sortState={extSort} onSort={handleExtSort} />
                  <SortableTableHeader label={`Net Price (${doc?.currency || 'USD'})`} field="net_price" sortState={extSort} onSort={handleExtSort} className="text-right" />
                  <SortableTableHeader label="Supplier" field="supplier_code" sortState={extSort} onSort={handleExtSort} />
                </tr>
              </thead>
              <tbody>
                {sortData((doc.line_items || []), extSort.field, extSort.dir).map((li, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 border text-center">{li.item_num}</td>
                    <td className="px-3 py-2 border">{li.item_description}</td>
                    <td className="px-3 py-2 border text-right">{li.quantity}</td>
                    <td className="px-3 py-2 border">{li.unit}</td>
                    <td className="px-3 py-2 border text-right">{(li.net_price || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 border">{li.supplier_code || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Approval Chain */}
            <h3 className="text-sm font-bold text-[#1e3a5f] mb-2">Approval Chain</h3>
            <div className="space-y-2 mb-6">
              {(doc.approval_chain || []).map((a, i) => (
                <div key={i} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${
                  a.step === doc.approver_step ? 'border-blue-300 bg-blue-50' : 'border-slate-200'
                }`}>
                  <div className={`w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center ${
                    a.status === 'APPROVED' ? 'bg-green-100 text-green-700' :
                    a.status === 'REJECTED' ? 'bg-red-100 text-red-700' :
                    a.step === doc.approver_step ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
                  }`}>{a.step}</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-800">{a.name}</p>
                    <p className="text-[11px] text-slate-500">{a.rule_name}</p>
                  </div>
                  <Badge variant="outline" className={`text-[10px] ${
                    a.status === 'APPROVED' ? 'bg-green-50 text-green-700 border-green-200' :
                    a.status === 'REJECTED' ? 'bg-red-50 text-red-700 border-red-200' : ''
                  }`}>{a.status}</Badge>
                </div>
              ))}
            </div>

            {/* Action Buttons */}
            {!mode ? (
              <div className="flex gap-3 justify-center py-4 border-t">
                <Button size="lg" className="bg-green-600 hover:bg-green-700 px-8" onClick={() => setMode('approve')} data-testid="ext-approve-btn">
                  <CheckCircle className="h-5 w-5 mr-2" /> Approve
                </Button>
                <Button size="lg" variant="destructive" className="px-8" onClick={() => setMode('reject')} data-testid="ext-reject-btn">
                  <XCircle className="h-5 w-5 mr-2" /> Reject
                </Button>
              </div>
            ) : (
              <div className="py-4 border-t space-y-3">
                <p className="text-sm font-semibold text-slate-700">
                  {mode === 'approve' ? 'Approval Notes (optional)' : 'Reason for Rejection *'}
                </p>
                <Textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={3}
                  placeholder={mode === 'reject' ? 'Please explain why you are rejecting this requisition...' : 'Optional comments...'}
                  className="text-sm"
                  data-testid="ext-approval-notes"
                />
                {mode === 'reject' && !notes.trim() && (
                  <p className="text-xs text-red-500">A reason is required when rejecting.</p>
                )}
                <div className="flex gap-3">
                  <Button
                    onClick={handleAction}
                    disabled={submitting || (mode === 'reject' && !notes.trim())}
                    className={mode === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                    variant={mode === 'reject' ? 'destructive' : 'default'}
                    data-testid="ext-confirm-action"
                  >
                    {submitting ? 'Processing...' : mode === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                  </Button>
                  <Button variant="outline" onClick={() => { setMode(null); setNotes(''); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-8 py-4 border-t bg-slate-50 rounded-b-2xl">
            <p className="text-[11px] text-slate-400 text-center">
              This is a secure approval link from Eyvo eProcurement. This link is encrypted and single-use.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
